import { useEffect, useRef, useState, useCallback } from "react";
import useAuth from "../context/AuthProvider";
import {
  StateReportModel,
  StateReportModel_Resp_Waiting_On_Client,
} from "../models/StateReportModel";
import { Box } from "@mui/system";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { MenuDropdown } from "../components/Elements/MenuDropdown";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Button,
  FormControl,
  Grid,
  LinearProgress,
  MenuItem,
  Select,
  SelectProps,
  Tab,
  Tabs,
} from "@mui/material";
import { Search, Close, Refresh } from "@mui/icons-material";
import { writeXlsxFile, formatUtcToLocal, mstDateStringToUtcIsoString } from "../helper/utils";
import { stateReportStatusMenuActions } from "../helper/stateReportStatusMenu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { GridFilterModel } from "@mui/x-data-grid";
import { StateReportList } from "../components/Lists/StateReportList";
import Swal from "sweetalert2";
import moment from "moment";
import StateReportMarkCompleted from "./StateReportMarkCompleted";
import { apiClient } from "../helper/api";
import formatPrice from "../helper/currencyFormatter";
import StateReportFilerNotes from "../components/Elements/stateReportFilerNotes";
import { AddEditStateReport } from "../components/AddEdit/AddEditStateReport";
import { Autocomplete, TextField, Typography } from "@mui/material";

export enum StateReportPageTabs {
  Outstanding = 1,
  Completed = 2,
}

interface ReportItem {
  dueDate?: string | null;
  setDueDate?: string | null;
}

export const StateReport = () => {
  const { user } = useAuth();
  const token = user?.token;
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState<number>(1);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<{ field: string; sort: string }[]>(
    [],
  );
  const [itemList, setItemList] = useState<StateReportModel[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedRows, setSelectedRows] = useState<StateReportModel[]>();
  const [openCompletedDialog, setOpenCompletedDialog] = useState<string>("");
  const [openNotesDialog, setOpenNotesDialog] = useState<boolean>(false);
  const [currentStatus, setCurrentStatus] = useState<string>("");
  const [openAddDialog, setOpenAddDialog] = useState<boolean>(false);
  const [newStateReportData, setNewStateReportData] = useState<
    StateReportModel | undefined
  >(undefined);

  // Filter Options & values
  const [partners, setPartners] = useState([
    { partnersId: 0, customerName: "" },
  ]);
  const [selectedPartner, setSelectedPartner] = useState<string>("0");
  const [states, setStates] = useState([{ stateId: 0, stateName: "" }]);
  const [selectedState, setSelectedState] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedDueDate, setSelectedDueDate] = useState<string>("");
  const [dueDates, setDueDates] = useState<ReportItem[]>([]);
  // Refs for filter values
  const selectedPartnerRef = useRef<string>("0");
  const selectedStateRef = useRef<number>(0);
  const selectedStatusRef = useRef<string>("");
  const selectedDueDateRef = useRef<string>("");
  
  // Ref to hold AbortController for cancelling pending requests
  const controllerRef = useRef<AbortController | null>(null);

  // Monotonically increasing counter — discard stale responses even when
  // abort() does not prevent an already-in-flight response from resolving.
  const requestGenerationRef = useRef<number>(0);
  
  // Ref to track previous selectedTab to prevent unnecessary calls
  const prevSelectedTabRef = useRef<number>(selectedTab);
  // Prevent fetches with stale state during tab switch resets.
  const isTabSwitchInProgressRef = useRef<boolean>(false);

  const statusFilterOptions = {
    1: [
      "Not Started",
      "Waiting on Client",
      "No Report Required",
      "Filed",
      "Revised",
    ], // Outstanding tab - includes all statuses for filtering
    2: ["Filed", "No Report Required"], // Completed tab - only show completed statuses
  };

  const exportOptions = ["Short", "Detailed"];
  const exportOptionsAdmin = ["Short", "Detailed", "All"];

  const keyNames: (keyof StateReportModel_Resp_Waiting_On_Client)[] = [
    "stateName",
    "name",
    "period",
    "dueDate",
    "payment",
    "report",
    "paymentType",
    "wetSignature",
    "notaryStamp",
    "licensee",
    "licenseNumber",
    "client",
    "mainLicensee",
    "zeroIndividual",
    "zeroEntity",
    "status",
    "dateFinalReportSentToClient",
    "largeReport",
    "firstRequest",
    "secondRequest",
    "thirdRequest",
    "notes",
    "taxAmount",
    "taxReported",
    "filerName",
  ];

  // API Functions
  const getStateReports = useCallback(
    async (pageNumber: number, _pageSize: number) => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      controllerRef.current = new AbortController();
      const signal = controllerRef.current.signal;
      const currentController = controllerRef.current;

      const myGeneration = ++requestGenerationRef.current;

      setLoading(true);
      const requestBody = {
        pageSize: _pageSize || 50,
        currentPage: pageNumber || 1,
        partnerId: selectedPartnerRef.current || "",
        stateId: selectedStateRef.current || "",
        status: selectedStatusRef.current || "",
        dueDate: selectedDueDateRef.current || "",
        field: sortModel?.[0]?.["field"] || "",
        sort: sortModel?.[0]?.["sort"] || "",
        fieldName: filterModel?.items?.[0]?.["columnField"] || "",
        value: (filterModel?.items?.[0]?.["value"] ?? "").toString().trim(),
        operator: filterModel?.items?.[0]?.["operatorValue"] || "",
        selectedTab: selectedTab || 0,
      };

      let wasAborted = false;
      try {
        const res = await apiClient(`/StateReport/getStateReport`, {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
          body: JSON.stringify(requestBody),
          signal,
        });

        if (myGeneration !== requestGenerationRef.current) {
          return null;
        }

        return res;
      } catch (err: any) {
        if (err.name === "AbortError") {
          wasAborted = true;
          return null;
        } else {
          console.error("API Error:", err);
          return null;
        }
      } finally {
        if (!wasAborted && controllerRef.current === currentController) {
          setLoading(false);
        }
      }
    },
    [token, sortModel, filterModel, selectedTab],
  );

  const fetchNewData = useCallback(async () => {
    const res = await getStateReports(currentPage, pageSize);

    if (!res) {
      return;
    }

    if (!res.data) {
      setItemList([]);
      setTotalCount(0);
      return;
    }

    setItemList(res.data);
    setTotalCount(res.totalCount);
  }, [currentPage, pageSize, getStateReports]);

  const fetchRequiredData = useCallback(
    async (action: string): Promise<any[]> => {
      try {
        const response = await apiClient(action, {
          method: "GET",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
        });

        return response?.data || [];
      } catch (error) {
        console.error(`Error fetching ${action}:`, error);
        return [];
      }
    },
    [token],
  );

  const setRequiredStates = useCallback(async () => {
    try {
      const [partners, dueDates, states] = await Promise.all([
        fetchRequiredData("/Partners/getPartners"),
        fetchRequiredData("/StateReport/getDueDates"),
        fetchRequiredData("/States/getStates"),
      ]);

      setPartners(partners);
      setDueDates(dueDates);
      setStates(states || []);
    } catch (error) {
      console.error("Error setting required states:", error);
    }
  }, [fetchRequiredData]);

  // Event Handlers
  const handleSubmitCompletedDialog = (completedDate: string) => {
    setLoading(true);
    const selectedRowsArray: StateReportModel[] = [];
    selectedRows?.forEach((m) => {
      m.dateFinalReportSentToClient = completedDate;
      m.partnerId = m.partnersId;
      selectedRowsArray.push(m);
    });

    apiClient(`/StateReports/SaveStateReport`, {
      method: "POST",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
      body: JSON.stringify(selectedRowsArray),
    })
      .then((res) => {
        if (!res) throw new Error("Something went wrong");
        setSelectedRows([]);
        Swal.fire({
          title: "Successful!",
          text: "Success",
          icon: "success",
          confirmButtonText: "OK",
        });
        fetchNewData();
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  const handleSubmitStatus = (status: string) => {
    const selectedRowsArray: StateReportModel[] = [];
    if (
      status === "Waiting on Client" ||
      status === "Filed" ||
      status === "No Report Required"
    ) {
      setOpenNotesDialog(true);
      setCurrentStatus(status);
      return;
    }

    setLoading(true);

    if (status === "Revised") {
      // Handle Revised status: Create duplicates with "Revised - " prefix
      selectedRows?.forEach((m) => {
        const revisedRecord = {
          ...m, // Copy all properties from original
          stateReportId: 0, // Reset ID so it creates a new record
          status: "Revised", // New duplicate has "Revised" status for filtering
          name: `Revised - ${m.name}`, // Add "Revised - " prefix to report name
          partnerId: m.partnersId,
          // Reset completion fields for the new record
          dateFinalReportSentToClient: undefined,
          taxAmount: "0",
          notes: "",
          // Keep other important fields
          dueDate: m.dueDate,
          period: m.period,
          licensee: m.licensee,
          licenseNumber: m.licenseNumber,
          stateName: m.stateName,
          client: m.client,
          payment: m.payment,
          report: m.report,
          largeReport: m.largeReport,
        };
        selectedRowsArray.push(revisedRecord);
      });
    } else {
      // Handle other statuses normally
      selectedRows?.forEach((m) => {
        m.status = status;
        m.partnerId = m.partnersId;
        if (status != "Filed" && status != "No Report Required") {
          m.dateFinalReportSentToClient = "";
        }
        selectedRowsArray.push(m);
      });
    }

    updateStatusApi(selectedRowsArray);
  };

  const handleCloseNotesDialog = () => {
    setOpenNotesDialog(false);
  };

  // Add New State Report handlers
  const handleOpenAddDialog = () => {
    // Initialize with empty/default data for a new state report
    const defaultStateReport: StateReportModel = {
      stateReportId: 0,
      scheduleId: 0,
      stateName: "", // Will be selected by user
      licensee: "", // Will be selected by user based on client + state
      licenseNumber: "", // Will be populated when licensee is selected
      client: "", // Will be selected first by user
      partnersId: 0,
      name: "", // Report name starts blank - user enters
      largeReport: false,
      largeReportFee: "",
      status: "Not Started",
      dueDate: "",
      period: "",
      notes: "",
      taxAmount: "",
      // Remove payment fields since they're set at universal level
      zeroEntity: "false",
      mainLicensee: "false",
    };
    setNewStateReportData(defaultStateReport);
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setNewStateReportData(undefined);
  };

  const handleSubmitAddDialog = () => {
    setOpenAddDialog(false);
    setNewStateReportData(undefined);
    // Refresh the data after adding a new state report
    fetchNewData();
  };

  const handleSubmitNotes = async (data: any) => {
    // Keep Filed/Filer Notes open with loading until API finishes (same as File Now Publish).
    const ok = await updateStatus(data);
    return ok;
  };

  const updateStatus = async (data: any): Promise<boolean> => {
    setLoading(true);
    const {
      status,
      noteDetail,
      policySubStatus,
      dateDeclarationFiled,
      notesDate,
      suspended,
      taxAmount,
      dateFinalReportSentToClient,
      largeReport,
      largeReportFee,
    } = data;
    const noteDate = formatUtcToLocal(new Date().toISOString(), "MM/DD/YYYY");

    const selectedRowsArray: StateReportModel_Resp_Waiting_On_Client[] = [];
    selectedRows?.forEach((m) => {
      m.dateFinalReportSentToClient = dateFinalReportSentToClient;
      m.largeReport = largeReport;
      m.largeReportFee = largeReportFee;

      // Preserve the stateReportId from original record for UPDATE vs INSERT logic
      const stateReportIdValue = m.stateReportId;

      const extendedRow = {
        ...m,
        status,
        partnerId: m.partnersId,
        stateReportId: stateReportIdValue, // Use resolved stateReportId value
        filerName:
          status === "Waiting on Client"
            ? (m.filerName ?? "")
            : `${user?.userFirstName ?? ""} ${user?.userLastName || user?.userlastName || ""}`.trim(),
        filerDate: mstDateStringToUtcIsoString(noteDate),
        notes: noteDetail,
        policySubStatus,
        dateDeclarationFiled,
        firstRequest: data.firstRequestDate,
        secondRequest: data.secondRequestDate,
        thirdRequest: data.thirdRequestDate,
        notesDate: (notesDate && typeof notesDate === 'string') ? mstDateStringToUtcIsoString(notesDate) : notesDate,
        suspended,
        taxAmount,
        largeReportFee,
      } as StateReportModel_Resp_Waiting_On_Client;

      // StateReportId preserved for proper UPDATE/INSERT logic

      selectedRowsArray.push(extendedRow);
    });
    return updateStatusApi(selectedRowsArray);
  };

  const updateStatusApi = async (data: any): Promise<boolean> => {
    try {
      // Check if we're creating new records (only for Revised status) or updating existing ones
      const isCreatingRevisedRecords = data.some(
        (record: any) =>
          record.status === "Revised" && record.stateReportId === 0,
      );
      // Check if this is a notes-related status update (has filerName or notes fields)
      const hasNotesData = data.some(
        (record: any) => record.filerName || record.notes,
      );

      // For notes-related updates, handle virtual vs existing records
      if (hasNotesData) {
        // Process the array to handle null stateReportId (virtual records)
        const processedData = data.map((record: any) => {
          if (
            record.stateReportId === null ||
            record.stateReportId === undefined ||
            record.stateReportId === 0
          ) {
            // Virtual record - needs INSERT, set stateReportId to 0
            return {
              ...record,
              stateReportId: 0,
            };
          }
          // Existing record - keep the existing stateReportId for UPDATE
          return record;
        });

        // Using AddEditStateReport endpoint for notes-related updates

        const res = await apiClient("/StateReport/AddEditStateReport", {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
          body: JSON.stringify(processedData),
        });

        if (res && res.status) {
          // CRITICAL FIX: Immediately update local state with returned stateReportId to prevent race condition
          if (res.data && Array.isArray(res.data)) {
            // Update itemList with the returned data containing correct stateReportIds
            setItemList((prevItemList) => {
              const updatedList = [...prevItemList];

              // For each returned record, update the corresponding item in our local list
              res.data.forEach((returnedRecord: any) => {
                const index = updatedList.findIndex(
                  (item) =>
                    item.rowNum === returnedRecord.rowNum ||
                    (item.partnersId === returnedRecord.partnersId &&
                      item.scheduleId === returnedRecord.scheduleId &&
                      item.stateName === returnedRecord.stateName),
                );

                if (index !== -1) {
                  // Update the existing record with the returned stateReportId
                  updatedList[index] = {
                    ...updatedList[index],
                    stateReportId:
                      returnedRecord.stateReportId ||
                      returnedRecord.StateReportId,
                    // Update other fields that might have changed
                    status: returnedRecord.status,
                    filerName: returnedRecord.filerName,
                    notes: returnedRecord.notes,
                    dateFinalReportSentToClient:
                      returnedRecord.dateFinalReportSentToClient,
                    taxAmount: returnedRecord.taxAmount,
                  };
                }
              });

              return updatedList;
            });

            // CRITICAL FIX: Also update selectedRows with the returned stateReportId
            setSelectedRows((prevSelectedRows) => {
              if (!prevSelectedRows) return prevSelectedRows;

              const updatedSelectedRows = [...prevSelectedRows];

              // For each returned record, update the corresponding item in selectedRows
              res.data.forEach((returnedRecord: any) => {
                const index = updatedSelectedRows.findIndex(
                  (item) =>
                    item.rowNum === returnedRecord.rowNum ||
                    (item.partnersId === returnedRecord.partnersId &&
                      item.scheduleId === returnedRecord.scheduleId &&
                      item.stateName === returnedRecord.stateName),
                );

                if (index !== -1) {
                  // Update the existing record with the returned stateReportId
                  updatedSelectedRows[index] = {
                    ...updatedSelectedRows[index],
                    stateReportId:
                      returnedRecord.stateReportId ||
                      returnedRecord.StateReportId,
                    // Update other fields that might have changed
                    status: returnedRecord.status,
                    filerName: returnedRecord.filerName,
                    notes: returnedRecord.notes,
                    dateFinalReportSentToClient:
                      returnedRecord.dateFinalReportSentToClient,
                    taxAmount: returnedRecord.taxAmount,
                  };
                }
              });

              return updatedSelectedRows;
            });
          }

          await Swal.fire({
            title: "Success!",
            text: "Status updated successfully.",
            icon: "success",
            confirmButtonText: "OK",
          });
          setSelectedRows([]);
          fetchNewData();
          setLoading(false);
          return true;
        }

        await Swal.fire({
          title: "Error!",
          text: "Failed to update status.",
          icon: "error",
          confirmButtonText: "OK",
        });
        setLoading(false);
        return false;
      }

      let endpoint;
      if (isCreatingRevisedRecords) {
        endpoint = "/StateReport/createRevisedStateReports";
      } else {
        endpoint = "/StateReport/updateStateReportsStatus";
      }

      // Using appropriate endpoint based on operation type

      const res = await apiClient(endpoint, {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }),
        body: JSON.stringify(data),
      });
      if (!res) throw new Error("Something went wrong");

      const successMessage = isCreatingRevisedRecords
        ? 'Revised state reports created successfully! They appear with "Revised" status and can be filtered accordingly.'
        : "Status updated successfully!";

      await Swal.fire({
        title: `${res.status ? "Successful!" : "Error!"}`,
        text: `${res.status ? successMessage : res.message}`,
        icon: `${res.status ? "success" : "error"}`,
        confirmButtonText: "OK",
      });
      if (res.status) {
        setSelectedRows([]);
        fetchNewData();
      }
      setLoading(false);
      return !!res.status;
    } catch (error) {
      console.log(error);
      setLoading(false);
      return false;
    }
  };

  const onExportXlsx = (
    values: StateReportModel[],
    option: string = "Detailed",
  ) => {
    setLoading(true);
    if (values.length === 0) {
      return;
    }

    // Define different column sets for different export options
    const getExportColumns = (
      exportOption: string,
    ): (keyof StateReportModel_Resp_Waiting_On_Client)[] => {
      switch (exportOption) {
        case "Short":
          return [
            "stateName",
            "name",
            "period",
            "dueDate",
            "status",
            "client",
            "licensee",
            "licenseNumber",
          ];
        case "Detailed":
          return keyNames; // Use all columns
        case "All":
          return keyNames; // Use all columns (same as Detailed for now)
        default:
          return keyNames;
      }
    };

    const exportColumns = getExportColumns(option);

    const headers = exportColumns.map((key) => {
      switch (key) {
        case "stateName":
          return "state";
        case "name":
          return "reportName";
        case "payment":
          return "paymentRequired";
        case "report":
          return "reportRequired";
        case "wetSignature":
          return "wetSignatureRequired";
        case "notes":
          return "notesToClient";
        case "taxAmount":
          return "taxAmount";
        case "taxReported":
          return "taxReported";
        default:
          return key;
      }
    });

    const items = values.map((item) => {
      const update: (string | number | Date | undefined)[] = [];

      exportColumns.forEach(
        (key: keyof StateReportModel_Resp_Waiting_On_Client) => {
          if (
            key === "dueDate" ||
            key === "dateFinalReportSentToClient" ||
            key === "firstRequest" ||
            key === "secondRequest" ||
            key === "thirdRequest"
          ) {
            const dateValue = item[key as keyof StateReportModel];
            update.push(
              dateValue
                ? formatUtcToLocal(dateValue as string, "MM/DD/YYYY")
                : "",
            );
          } else if (key === "taxReported" || key === "taxAmount") {
            const value = item[key as keyof StateReportModel] ?? "0";
            update.push(formatPrice(value as string));
          } else {
            update.push(
              item[key as keyof StateReportModel] as
                | string
                | number
                | undefined,
            );
          }
        },
      );

      return update;
    });

    const updates = [headers, ...items];

    // Generate filename based on export option
    const getFilename = (exportOption: string) => {
      switch (exportOption) {
        case "All":
          return "All_ExportedStateReport.xlsx";
        case "Short":
          return "Short_StateReport.xlsx";
        case "Detailed":
          return "Detailed_StateReport.xlsx";
        default:
          return "StateReport.xlsx";
      }
    };

    writeXlsxFile(updates, getFilename(option));
    setLoading(false);
  };

  const handleExport = (option: string) => {
    try {
      setLoading(true);

      // Export logic based on option
      if (option === "All") {
        // Export all data across all pages (ignores selected rows)
        // Use a large page size to ensure we get all records that match current filters
        getStateReports(1, totalCount || 10000)
          .then((response) => {
            const responseData = response.data;

            if (responseData && responseData.length > 0) {
              onExportXlsx(responseData, option);
              Swal.fire({
                title: "Export Successful!",
                text: `All ${responseData.length} records have been exported.`,
                icon: "success",
                confirmButtonText: "OK",
              });
            } else {
              alert("No data available for export");
            }
            setLoading(false);
          })
          .catch((error) => {
            setLoading(false);
            console.error("Error occurred during export:", error);
          });
      } else if (selectedRows && selectedRows.length > 0) {
        // Export selected rows for Short and Detailed options
        onExportXlsx(selectedRows, option);
        Swal.fire({
          title: "Export Successful!",
          text: `${selectedRows.length} selected records have been exported.`,
          icon: "success",
          confirmButtonText: "OK",
        });
        setLoading(false);
      } else {
        // Export only current page data for Short and Detailed options
        if (itemList && itemList.length > 0) {
          onExportXlsx(itemList, option);
          Swal.fire({
            title: "Export Successful!",
            text: `${itemList.length} records from current view have been exported.`,
            icon: "success",
            confirmButtonColor: "#02A4E3",
            confirmButtonText: "OK",
          });
        } else {
          alert("No data available for export");
        }
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.error("Error occurred during export:", error);
    }
  };

  const handleSearch = useCallback(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      void fetchNewData();
    }
  }, [currentPage, fetchNewData]);

  const clearSearch = () => {
    setSelectedState(0);
    setSelectedPartner("0");
    setSelectedStatus("");
    setSelectedDueDate("");
    selectedPartnerRef.current = "0";
    selectedStateRef.current = 0;
    selectedStatusRef.current = "";
    selectedDueDateRef.current = "";
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      void fetchNewData();
    }
  };

  // Custom Select Components
  const FMSelectCustomCustomer: React.FC<SelectProps> = () => (
    <div>
      <FormControl fullWidth>
        <Autocomplete
          sx={{
            marginTop: '-4px !important',
            '& .MuiAutocomplete-input': { padding: '0px 4px 0px 5px !important' },
          }}
          options={partners}
          getOptionLabel={(option) => option.customerName}
          onChange={(_, value) => {
            const id = value?.partnersId.toString() ?? "0";
            selectedPartnerRef.current = id;
            setSelectedPartner(id);
          }}
          value={partners.find(item => item.partnersId.toString() === selectedPartner)}
          renderInput={(params) => <TextField {...params} label="Client" InputLabelProps={{ ...params.InputLabelProps, shrink: (parseInt(selectedPartner) > 0 || (typeof params.inputProps.value === 'string' && params.inputProps.value.length > 0)) ? true : false }}
            sx={{
              '.MuiFormLabel-root.Mui-focused': {
                color: (typeof params.inputProps.value === 'string' && params.inputProps.value.length > 0) ? '#000' : '#cccccc !important'
              },
              '& .MuiInputBase-input': { padding: '4.5px 9px !important', height: '19px' },
              '& .MuiInputBase-root': { marginTop: '5px !important' },
              '& .MuiOutlinedInput-root': { padding: '4.5px 9px' },
              '& .MuiFormLabel-root': { transform: 'translate(14px, 13px) scale(1)' },
              '& .MuiInputLabel-shrink': { transform: 'translate(14px, -2px) scale(0.75)' },
            }}
          />}
        />
        {/* <Select
          onChange={(event) => {
            setSelectedPartner(event.target.value);
            selectedPartnerRef.current = event.target.value;
          }}
          value={selectedPartner}
          id="test-select-label"
          autoWidth
          size="small"
          label={null}
          className="border-round custom-menu-item"
          IconComponent={ExpandMoreIcon}
          displayEmpty
        >
          <MenuItem key={0} value={0} className="custom-menu-item">
            Client
          </MenuItem>
          {partners?.map((item, key) => (
            <MenuItem
              key={key}
              className="custom-menu-item"
              value={item.partnersId}
            >
              {item.customerName}
            </MenuItem>
          ))}
        </Select> */}
      </FormControl>
    </div>
  );

  const FMSelectStateCustom: React.FC<SelectProps> = () => (
    <FormControl fullWidth>
      <Autocomplete
        sx={{
          marginTop: '-4px !important',
          '& .MuiAutocomplete-input': { padding: '0px 4px 0px 5px !important' },
        }}
        options={states}
        getOptionLabel={(option) => option.stateName}
        onChange={(_, value) => {
          const id = value?.stateId ?? 0;
          selectedStateRef.current = id;
          setSelectedState(id);
        }}
        value={states.find(item => item.stateId === selectedState)}
        renderInput={(params) => <TextField {...params} label="Home State" InputLabelProps={{ ...params.InputLabelProps, shrink: (selectedState > 0 || (typeof params.inputProps.value === 'string' && params.inputProps.value.length > 0)) ? true : false }}
          sx={{
            '.MuiFormLabel-root.Mui-focused': {
              color: (typeof params.inputProps.value === 'string' && params.inputProps.value.length > 0) ? '#000' : '#cccccc !important'
            },
            '& .MuiInputBase-input': { padding: '4.5px 9px !important', height: '19px' },
            '& .MuiInputBase-root': { marginTop: '5px !important' },
            '& .MuiOutlinedInput-root': { padding: '4.5px 9px' },
            '& .MuiFormLabel-root': { transform: 'translate(14px, 13px) scale(1)' },
            '& .MuiInputLabel-shrink': { transform: 'translate(14px, -2px) scale(0.75)' },
          }}
        />}
      />
      {/* <Select
        onChange={(event) => {
          selectedStateRef.current = Number(event.target.value);
          setSelectedState(Number(event.target.value));
        }}
        value={selectedState}
        size="small"
        id="test-select-label"
        label={null}
        className="border-round custom-menu-item"
        IconComponent={ExpandMoreIcon}
        displayEmpty
      >
        <MenuItem value={"0"} className="custom-menu-item">
          Home State
        </MenuItem>
        {states?.map((state, key) => (
          <MenuItem
            key={key}
            value={state.stateId}
            className="custom-menu-item"
          >
            {state.stateName}
          </MenuItem>
        ))}
      </Select> */}
    </FormControl>
  );

  // Item 6: add missing "Status" label on the Filing Queue status filter
  const FMSelectStatus: React.FC<SelectProps> = () => (
    <div>
      <FormControl fullWidth>
        <Select
          onChange={(event) => {
            selectedStatusRef.current = event.target.value;
            setSelectedStatus(event.target.value);
            handleSearch();
          }}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c4c4c4" },
            "& .MuiInputBase-root": { marginTop: '1px !important' },
            "& .MuiInputBase-input": { padding: '7px 14px !important' },
            // Item 6: match the Client / Home State label font (.MuiFormLabel-root: 14px / 600)
            "& .MuiSelect-select": { fontSize: '14px', fontWeight: 600 },
          }}
          value={selectedStatus}
          id="status-select"
          // autoWidth
          size="small"
          label={null}
          renderValue={(selected) => (
            <span style={{ color: selected ? 'inherit' : 'rgba(0, 0, 0, 0.6)' }}>
              {(selected as string) || 'Status'}
            </span>
          )}
          className="border-round"
          IconComponent={ExpandMoreIcon}
          displayEmpty
        >
         {selectedTab === 2 && selectedRows && selectedRows.length > 0 && !selectedRows.some(row => 
            row.isDoneByClient === true || String(row.isDoneByClient) === "true"
          ) && <MenuItem key={""} value={""} className="custom-menu-item">
            Status
          </MenuItem>
          }
          {statusFilterOptions[
            selectedTab as keyof typeof statusFilterOptions
          ].map((item, key) => (
            <MenuItem key={key} className="custom-menu-item" value={item}>
              {item}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );

  const FMSelectDate: React.FC<{ dueDates: ReportItem[] }> = ({ dueDates }) => (
    <FormControl fullWidth>
      {/* <Autocomplete
        options={dueDates}
        getOptionLabel={(option) => option.dueDate ?? ""}
        onChange={(event, value) => setSelectedDueDate(value?.dueDate ?? "")}
        value={dueDates.find(item => item.dueDate === selectedDueDate)}
        renderInput={(params) => <TextField {...params} label="Due Date" InputLabelProps={{ ...params.InputLabelProps, shrink: (selectedDueDate !== "" || (typeof params.inputProps.value === 'string' && params.inputProps.value.length > 0)) ? true : false }}
          sx={{
            '.MuiFormLabel-root.Mui-focused': {
              color: (typeof params.inputProps.value === 'string' && params.inputProps.value.length > 0) ? '#000' : '#cccccc !important'
            },
            '& .MuiInputBase-input': { padding: '4.5px 9px !important' },
            '& .MuiInputBase-root': { marginTop: '5px !important' },
            '& .MuiOutlinedInput-root': { padding: '4.5px 9px' },
            '& .MuiFormLabel-root': { transform: 'translate(14px, 13px) scale(1)' },
            '& .MuiInputLabel-shrink': { transform: 'translate(14px, -2px) scale(0.75)' },
          }}
        />}
      /> */}
      <Select
        onChange={(event) => {
          setSelectedDueDate(event.target.value);
          selectedDueDateRef.current = event.target.value;
        }}
        sx={{
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c4c4c4" },
          "& .MuiInputBase-root": { marginTop: '1px !important' },
          "& .MuiInputBase-input": { padding: '7px 14px !important' },
          // Item 6: match the Client / Home State label font (.MuiFormLabel-root: 14px / 600)
          "& .MuiSelect-select": { fontSize: '14px', fontWeight: 600 },
        }}
        value={selectedDueDate || ""}
        id="date-select-label"
        // autoWidth
        size="small"
        className="border-round"
        IconComponent={ExpandMoreIcon}
        displayEmpty
        renderValue={(selected) => (
          <span style={{ color: selected ? 'inherit' : 'rgba(0, 0, 0, 0.6)' }}>
            {selected ? formatUtcToLocal(selected as string, "MM/DD/YYYY") : 'Due Date'}
          </span>
        )}
      >
        <MenuItem key="default" value="">
          Due Date
        </MenuItem>
        {dueDates?.map(
          (item, index) =>
            item.dueDate && (
              <MenuItem
                key={index}
                className="custom-menu-item"
                value={item.dueDate}
              >
                {item.dueDate
                  ? formatUtcToLocal(item.dueDate, "MM/DD/YYYY")
                  : "Invalid Date"}
              </MenuItem>
            ),
        )}
      </Select>
    </FormControl>
  );

  // Effects
  useEffect(() => {
    setRequiredStates();
  }, [setRequiredStates]);

  useEffect(() => {
    // Only run when tab actually changes
    if (prevSelectedTabRef.current === selectedTab) {
      return;
    }
    isTabSwitchInProgressRef.current = true;
    requestGenerationRef.current += 1;

    // Cancel any pending request when tab changes
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    
    // Update ref
    prevSelectedTabRef.current = selectedTab;
    
    setSelectedRows([]);
    setCurrentStatus("");
    setOpenNotesDialog(false);
    setOpenCompletedDialog("");
    setSelectedPartner("0");
    setSelectedState(0);
    setSelectedStatus("");
    setSelectedDueDate("");
    selectedPartnerRef.current = "0";
    selectedStateRef.current = 0;
    selectedStatusRef.current = "";
    selectedDueDateRef.current = "";
    setFilterModel({ items: [] });
    setSortModel([]);
    setCurrentPage(1);
    setPageSize(25);
    setTotalCount(0);
    setItemList([]);
    
    queueMicrotask(() => {
      isTabSwitchInProgressRef.current = false;
    });
    
  }, [selectedTab]);

  useEffect(() => {
    if (isTabSwitchInProgressRef.current) {
      return;
    }
    fetchNewData();
  }, [fetchNewData, pageSize, currentPage, sortModel, filterModel]);

  // Cleanup: Cancel any pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
    };
  }, []);

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <div style={{ position: "fixed", top: "0", left: "0", zIndex: "9990", width: "100%" }}>
          {/* {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />} */}
        </div>
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              // justifyContent: "flex-end",
              alignItems: "center",
              gap: 1.5,
              px: 2,
              py: 1.5,
              borderBottom: "1px solid #e2e8f0",
              backgroundColor: "#fafbfc",
              minHeight: 56,
              marginLeft: { xs: '-24px', md: '0px' },
              marginRight: { xs: '-24px', md: '0px' },
            }}
          >
            {/* Tabs: desktop only (md and up) */}
            <Tabs
              value={selectedTab}
              onChange={(_event, newValue: number) => {
                setItemList([]);
                setSelectedTab(newValue);
              }}
              aria-label="State Report Tabs"
              sx={{
                display: { xs: "none", md: "flex" },
                minHeight: 48,
                "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.9375rem" },
                "& .Mui-selected": { color: "#2099d2" },
                "& .MuiTabs-indicator": { backgroundColor: "#2099d2", height: 3, borderRadius: "3px 3px 0 0" },
              }}
            >
              <Tab label="Outstanding Queue" value={StateReportPageTabs.Outstanding} />
              <Tab label="Completed Queue" value={StateReportPageTabs.Completed} />
            </Tabs>
            {/* Select option for mobile/tablet view */}
            <FormControl
              size="small"
              sx={{
                display: { xs: "block", md: "none" },
                minWidth: 160,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                "& .MuiSelect-select": { fontWeight: 600, color: "#2099d2", py: 0.9 },
              }}
            >
              <Select
                value={selectedTab}
                onChange={(e) => {
                  setItemList([]);
                  setSelectedTab(Number(e.target.value));
                }}
                displayEmpty
                sx={{ textTransform: "none", fontSize: "0.9375rem" }}
              >
                <MenuItem value={StateReportPageTabs.Outstanding}>Outstanding Queue</MenuItem>
                <MenuItem value={StateReportPageTabs.Completed}>Completed Queue</MenuItem>
              </Select>
            </FormControl>
            
          </Box>
          <Box
            sx={{
              width: "100%",
              py: 2,
              px: 2,
              // backgroundColor: "#ffffff",
              // borderBottom: "1px solid #e2e8f0",
            }}
          >
            <Grid container spacing={2} className="filter-grid" display="flex">
                <Grid item xl={2} lg={4} sm={6} xs={12}>
                  {(user?.userRoles.split(",").includes("Super Admin") ||
                    user?.userRoles.split(",").includes("MISC State Reporter")) && (
                    <FMSelectCustomCustomer />
                  )}
                </Grid>
                <Grid item xl={2} lg={4} sm={6} xs={12}>
                  <FMSelectStateCustom />
                </Grid>
                <Grid item xl={2} lg={4} sm={6} xs={12}>
                  <FMSelectStatus />
                </Grid>
                <Grid item xl={2} lg={4} sm={6} xs={12}>
                  <FMSelectDate dueDates={dueDates} />
                </Grid>
                <Grid item xs={12} sm="auto">
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                      variant="contained"
                      size="medium"
                      onClick={handleSearch}
                      startIcon={<Search />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        backgroundColor: "#2099d2",
                        "&:hover": { backgroundColor: "#176990" },
                      }}
                    >
                      Search
                    </Button>
                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={clearSearch}
                      startIcon={<Close />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderColor: "#e2e8f0",
                        color: "#475569",
                        "&:hover": { borderColor: "#2099d2", color: "#2099d2" },
                      }}
                    >
                      Clear
                    </Button>
                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={() => window.location.reload()}
                      startIcon={<Refresh />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderColor: "#e2e8f0",
                        color: "#475569",
                        "&:hover": {
                          borderColor: "#2099d2",
                          backgroundColor: "#2099d214",
                          color: "#2099d2",
                        },
                      }}
                    >
                      Refresh
                    </Button>
                  </Box>
                </Grid>
            </Grid>
          </Box>
        </Box>

        <Box sx={{ height: "calc(100vh - 300px)", display: "flex", flexDirection: "column", width: "100%", borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 24px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)', border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, py: 1.5, px: 1 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#334155', mr: 'auto', pl: 1 }}>State Report</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {selectedRows?.length !== 0 &&
                selectedRows?.length !== undefined && (
                  <div className="dropdowm-class ms-2" style={{ zIndex: "9" }}>
                    <MenuDropdown
                      menuTitle="Status"
                      items={stateReportStatusMenuActions(selectedTab, selectedRows)}
                      onItemClick={handleSubmitStatus}
                      variant="outlined"
                      size="medium"
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderColor: "#e2e8f0",
                        color: "#475569",
                        "&:hover": {
                          borderColor: "#2099d2",
                          backgroundColor: "#2099d214",
                          color: "#2099d2",
                        },
                      }}
                    />
                  </div>
                )}
              <Button
                variant="contained"
                size="small"
                onClick={handleOpenAddDialog}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor: "#2099d2",
                  "&:hover": { backgroundColor: "#176990" },
                }}
              >
                Add New
              </Button>
              <MenuDropdown
                menuTitle="Export"
                items={
                  user?.userRoles.split(",").includes("Super Admin")
                    ? exportOptionsAdmin
                    : exportOptions
                }
                onItemClick={handleExport}
                variant="outlined"
                size="medium"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: "#e2e8f0",
                  color: "#475569",
                  "&:hover": {
                    borderColor: "#2099d2",
                    backgroundColor: "#2099d214",
                    color: "#2099d2",
                  },
                }}
              />
            </Box>
          </Box>
          <StateReportList
            itemList={itemList}
            totalCount={totalCount}
            filterModel={filterModel}
            setFilterModel={setFilterModel}
            setSortModel={setSortModel}
            sortModel={sortModel}
            selectedTab={selectedTab}
            setSelectedRows={setSelectedRows}
            setCurrentPage={setCurrentPage}
            setPageSize={setPageSize}
            _currentPage={currentPage}
            _pageSize={pageSize}
            selectedRows={selectedRows}
            onRefresh={fetchNewData}
            states={states}
            setSelectedTab={setSelectedTab}
            loading={loading}
            setLoading={setLoading}
          />
        </Box>

        

        <Box>
          <StateReportFilerNotes
            open={openNotesDialog}
            handleClose={handleCloseNotesDialog}
            handleSubmit={handleSubmitNotes}
            selectedRows={selectedRows}
            currentStatus={currentStatus}
          />
        </Box>
      </LocalizationProvider>

      {openCompletedDialog && (
        <StateReportMarkCompleted
          open={!!openCompletedDialog}
          handleClose={() => setOpenCompletedDialog("")}
          handleSubmit={handleSubmitCompletedDialog}
          selectedRows={selectedRows}
        />
      )}

      <AddEditStateReport
        handleClose={handleCloseAddDialog}
        Open={openAddDialog}
        StateRuleMatrixCL={newStateReportData}
        handleSubmit={handleSubmitAddDialog}
        isStateReport={true}
      />

      
    </>
  );
};
